<!--
@component
MatchCardMatchResult

See markup below.
-->

<script lang="ts">
  import type { Match } from '$lib/@types/opta';
  import { onMount } from 'svelte';
  import AnimatedScore from '$lib/components/common/AnimatedScore/index.svelte';
  import LiveBug from '$lib/components/common/LiveBug/index.svelte';
  import Team from './Team.svelte';
  import Goals from './Goals.svelte';
  import {
    getAwayTeam,
    getHomeTeam,
    isOvertime,
    getMatchTime,
    isPlaying,
    getHomeScore,
    getAwayScore,
    getHomeGoalsAndRedCards,
    getAwayGoalsAndRedCards,
  } from '$utils/getMatchStats';

  type Props = {
    match: Match;
    flagLink?: boolean;
    /** Kept for backwards compatibility; not used internally. */
    isFixture?: boolean;
    /** Kept for backwards compatibility; not used internally. */
    liveData?: unknown;
    live?: boolean;
  };

  // `isFixture` and `liveData` are accepted but unused; the parent
  // `<MatchCard>` already routes fixtures to `<MatchSked>`, and live data
  // is read off the `match` object via `$utils/getMatchStats`. They're
  // preserved here so callers passing them continue to work.
  let {
    match,
    flagLink = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isFixture: _isFixture = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    liveData: _liveData = undefined,
    live = $bindable(),
  }: Props = $props();

  const homeTeam = $derived(getHomeTeam(match));
  const awayTeam = $derived(getAwayTeam(match));

  // Pre-formatted `{ month, day, time }` decorated onto each match by
  // `getData()` (see `$utils/data/endpoints.js`) — no per-render date math.
  const matchDate = $derived(match.localDateStamp);

  const isLive = $derived(!!isPlaying(match));
  const overtime = $derived(isOvertime(match));
  const timeToShow = $derived(getMatchTime(match));

  const homeScore = $derived(getHomeScore(match));
  const awayScore = $derived(getAwayScore(match));

  const homeEvents = $derived(getHomeGoalsAndRedCards(match));
  const awayEvents = $derived(getAwayGoalsAndRedCards(match));

  const hasGoals = $derived(homeEvents.length > 0 || awayEvents.length > 0);
  const isGroupStage = $derived(match.matchInfo?.stage?.name === 'Group Stage');

  // Sync bindable `live` prop with derived live state.
  $effect(() => {
    live = isLive;
  });

  // Local date stamp depends on the viewer's timezone, so SSR can render
  // a slightly different value than the hydrated client. Defer showing
  // it until after mount so the layout doesn't visibly shift.
  let mounted = $state(false);
  onMount(() => {
    mounted = true;
  });

  let cardWidth = $state(0);
  let cardHeight = $state(0);
</script>

<div
  class="match-card-wrapper"
  class:isLive
  class:group={isGroupStage}
  class:noGoals={!hasGoals}
  bind:clientWidth={cardWidth}
  bind:clientHeight={cardHeight}
  style="--cardWidth: {cardWidth}px; --cardHeight: {cardHeight}px"
>
  <!-- Results ------------------------------------------------------- -->
  <div class="results-wrapper" class:isLive>
    <div class="date-time" class:live={isLive} class:no-matchtime={!timeToShow}>
      {#if isLive}
        <div class="time">{timeToShow}</div>
        <LiveBug />
      {:else}
        <div class="date">
          {#if !mounted || !matchDate}
            &hellip;
          {:else}
            <span class="day">{matchDate.day}</span>
            <span class="month">{matchDate.month}</span>
          {/if}
        </div>
      {/if}
    </div>

    <Team team={homeTeam} side="home" {flagLink} />
    <Team team={awayTeam} side="away" {flagLink} />

    <!-- Scores -->
    <div class="scores">
      <div class="scores-results">
        <div class="team-score">
          <AnimatedScore value={homeScore?.score ?? 0} bold />
          {#if homeScore?.pen}
            <div class="pen">({homeScore.pen})</div>
          {/if}
        </div>
        <div class="score-separator">&middot;</div>
        <div class="team-score">
          <AnimatedScore value={awayScore?.score ?? 0} bold />
          {#if awayScore?.pen}
            <div class="pen">({awayScore.pen})</div>
          {/if}
        </div>
      </div>
      {#if overtime}
        <div class="scores-overtime">AET</div>
      {/if}
    </div>
  </div>

  <!-- Goals --------------------------------------------------------- -->
  {#if hasGoals}
    <div class="goals">
      <div class="goals-container">
        <div class="home-goals">
          <Goals events={homeEvents} />
        </div>
      </div>

      <div
        class="goals-divider"
        style="height:{1.5 * Math.max(homeEvents.length, awayEvents.length)}rem"
      ></div>

      <div class="goals-container">
        <div class="away-goals">
          <Goals events={awayEvents} />
        </div>
      </div>
    </div>
  {/if}
</div>

<style lang="scss">
  @use '../../../styles/mixins' as mixins;
  @use 'layout' as *;

  .date-time {
    margin-top: 1.5rem;
    border-radius: calc(1.75 * var(--theme-style-border-radius));

    .time {
      @include mixins.text-sm;
      @include mixins.fmb-1;
      @include mixins.font-semibold;
    }

    @media (max-width: 475px) {
      margin-top: 1rem;
    }

    &.no-matchtime {
      .time {
        display: none;
      }

      &.live {
        margin-top: 2.5rem;

        @media (max-width: 475px) {
          margin-top: 1.5rem;
        }
      }
    }
  }

  .match-card-wrapper.noGoals {
    .results-wrapper {
      border-radius: var(--theme-style-border-radius);
    }
  }

  .match-card-wrapper.isLive {
    @include mixins.hard-shadow;

    .team-score,
    .pen {
      color: var(--theme-colour-live-red);
    }
  }

  .goals {
    width: 100%;
    height: 100%;
    display: flex;
    position: relative;
    justify-content: center;
    align-items: flex-start;

    @include mixins.fmx-auto;
    @include mixins.fmy-4;
    @include mixins.fpb-4;

    .goals-divider {
      width: 2px;
      background: var(--theme-colour-background);
      @include mixins.hard-shadow;
      border-radius: calc(0.5 * var(--theme-style-border-radius));
    }

    .goals-container {
      flex: 1;
    }

    .home-goals,
    .away-goals {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
</style>
